import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { RAZORPAY_CHECKOUT_CONFIG } from "../config/razorpay.config";
import { useScrollLock } from "../context/ScrollLockContext";
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaUsers,
  FaBed,
  FaCalendarAlt,
  FaClock,
  FaCheckCircle,
  FaShieldAlt,
  FaPercentage,
  FaStar,
  FaWifi,
  FaSnowflake,
  FaTv,
  FaCoffee,
} from "react-icons/fa";
import jwtTokenService from "../services/jwtTokenService";
import { API_CONFIG } from "../config/api.config";
import { APP_URLS } from "../config/urls.config";

const RoomBookingPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lockScroll } = useScrollLock();

  // Get data passed from navigation
  const passedRoom = location.state?.room;
  const passedHotel = location.state?.hotel;

  const [room, setRoom] = useState(passedRoom || null);
  const [hotel, setHotel] = useState(passedHotel || null);
  const [loading, setLoading] = useState(!passedRoom);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    checkInDate: "",
    checkInTime: "",
    checkOutDate: "",
    checkOutTime: "",
    guests: 1,
    fullName: "",
    email: "",
    phone: "",
    specialRequests: "",
  });

  const [userData, setUserData] = useState(null);
  const [membershipTier, setMembershipTier] = useState("Basic");
  const [taxSettings, setTaxSettings] = useState({
    isTaxEnabled: false,
    isInclusive: false,
    taxPercentage: 0,
    taxName: "GST",
    taxType: "PERCENTAGE",
  });

  const resolvedHotelId = room?.hotel?.hotelId || room?.hotelId || hotel?.hotelId || null;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    // Get user data
    const user = jwtTokenService.getUser();
    setUserData(user);

    // Fetch user's current membership tier from backend
    const fetchMembershipTier = async () => {
      if (user && (user.id || user.user_id || user.userId)) {
        try {
          const userId = user.id || user.user_id || user.userId;
          const response = await fetch(
            `${API_CONFIG.BASE_URL}/profile/current-plan/${userId}`,
          );
          if (response.ok) {
            const data = await response.json();
            setMembershipTier(data.membershipTier || data.planType || "Basic");
          }
        } catch (error) {
          console.error("Error fetching membership tier:", error);
          setMembershipTier("Basic");
        }
      }
    };

    fetchMembershipTier();

    // Pre-fill form with user data
    if (user) {
      setFormData((prev) => ({
        ...prev,
        fullName: user.name || user.fullName || "",
        email: user.email || "",
        phone: user.phone || user.mobile || "",
      }));
    }

    // If data wasn't passed, fetch it
    if (!passedRoom) {
      fetchBookingDetails();
    }

    return () => {
      // Cleanup: remove script on unmount
      document.body.removeChild(script);
    };
  }, [roomId]);

  useEffect(() => {
    const fetchTaxSettings = async () => {
      if (!resolvedHotelId) {
        setTaxSettings({
          isTaxEnabled: false,
          isInclusive: false,
          taxPercentage: 0,
          taxName: "GST",
          taxType: "PERCENTAGE",
        });
        return;
      }

      try {
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/hotels/${resolvedHotelId}/room-tax-settings`,
        );

        if (!response.ok) {
          throw new Error("Failed to load room tax settings");
        }

        const data = await response.json();
        setTaxSettings({
          isTaxEnabled: Boolean(data.isTaxEnabled),
          isInclusive: Boolean(data.isInclusive),
          taxPercentage: Number(data.taxPercentage || 0),
          taxName: data.taxName || "GST",
          taxType: data.taxType || "PERCENTAGE",
        });
      } catch (error) {
        console.error("Error fetching room tax settings:", error);
        setTaxSettings({
          isTaxEnabled: false,
          isInclusive: false,
          taxPercentage: 0,
          taxName: "GST",
          taxType: "PERCENTAGE",
        });
      }
    };

    fetchTaxSettings();
  }, [resolvedHotelId]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      // Fetch room details
      // Add your API call here
      toast.error("Room data not found");
      navigate("/HotelRoomsPage");
    } catch (error) {
      console.error("Error fetching booking details:", error);
      toast.error("Failed to load booking details");
      navigate("/HotelRoomsPage");
    } finally {
      setLoading(false);
    }
  };

  const getCheckInTimeSlots = () => {
    const slots = [];
    // 12 PM to 11:55 PM (check-in times)
    for (let hour = 12; hour <= 23; hour++) {
      for (let min = 0; min < 60; min += 30) {
        const time = `${hour.toString().padStart(2, "0")}:${min
          .toString()
          .padStart(2, "0")}`;
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const period = hour >= 12 ? "PM" : "AM";
        const displayTime = `${displayHour}:${min
          .toString()
          .padStart(2, "0")} ${period}`;
        slots.push({ value: time, label: displayTime });
      }
    }
    return slots;
  };

  const getCheckOutTimeSlots = () => {
    const slots = [];
    // 12:05 AM to 11:55 AM (check-out times)
    // Add times from 00:05 to 11:55
    for (let hour = 0; hour <= 11; hour++) {
      for (let min = 5; min < 60; min += 30) {
        if (hour === 0 && min === 5) {
          // Start from 12:05 AM
          const time = `00:05`;
          const displayTime = `12:05 AM`;
          slots.push({ value: time, label: displayTime });
        } else if (hour > 0) {
          const time = `${hour.toString().padStart(2, "0")}:${min
            .toString()
            .padStart(2, "0")}`;
          const displayTime = `${hour}:${min.toString().padStart(2, "0")} AM`;
          slots.push({ value: time, label: displayTime });
        }
      }
    }
    // Add 11:55 AM at the end
    slots.push({ value: "11:55", label: "11:55 AM" });
    return slots;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const calculateNights = () => {
    if (formData.checkInDate && formData.checkOutDate) {
      const checkIn = new Date(formData.checkInDate);
      const checkOut = new Date(formData.checkOutDate);
      const diffTime = Math.abs(checkOut - checkIn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 1;
  };

  const getMembershipDiscount = () => {
    switch (membershipTier.toUpperCase()) {
      case "PLATINUM":
        return 15;
      case "GOLD":
        return 10;
      case "SILVER":
        return 5;
      default:
        return 0;
    }
  };

  const calculateTotal = () => {
    const nights = calculateNights();
    const roomPrice = Number(room?.pricePerNight || 200);
    const basePrice = roomPrice * nights;

    // Calculate discount based on membership tier (matching backend logic)
    let discountPercentage = 0;
    switch (membershipTier.toUpperCase()) {
      case "PLATINUM":
        discountPercentage = 0.15; // 15%
        break;
      case "GOLD":
        discountPercentage = 0.1; // 10%
        break;
      case "SILVER":
        discountPercentage = 0.05; // 5%
        break;
      default:
        discountPercentage = 0; // Basic - no discount
    }

    const discount = parseFloat((basePrice * discountPercentage).toFixed(2));
    const priceAfterDiscount = basePrice - discount;
    const taxEnabled = Boolean(taxSettings?.isTaxEnabled);
    const taxInclusive = Boolean(taxSettings?.isInclusive);
    const taxPercentage = Number(taxSettings?.taxPercentage || 0);

    let taxAmount = 0;
    let amountBeforePlatform = priceAfterDiscount;

    if (taxEnabled && taxPercentage > 0) {
      if (taxInclusive) {
        taxAmount = parseFloat(
          (
            priceAfterDiscount -
            priceAfterDiscount / (1 + taxPercentage / 100)
          ).toFixed(2),
        );
      } else {
        taxAmount = parseFloat(
          ((priceAfterDiscount * taxPercentage) / 100).toFixed(2),
        );
        amountBeforePlatform = parseFloat(
          (priceAfterDiscount + taxAmount).toFixed(2),
        );
      }
    }

    const platformFee = parseFloat((priceAfterDiscount * 0.025).toFixed(2)); // 2.5% Platform Fee (exact decimal)
    const total = parseFloat((amountBeforePlatform + platformFee).toFixed(2));

    return {
      basePrice,
      discount,
      discountPercentage: discountPercentage * 100, // Return as percentage number
      amountAfterDiscount: parseFloat(priceAfterDiscount.toFixed(2)),
      taxEnabled,
      taxInclusive,
      taxPercentage,
      taxName: taxSettings?.taxName || "GST",
      taxAmount,
      amountBeforePlatform,
      platformFee,
      total,
      nights,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userData) {
      toast.error("Please login to book a room");
      navigate("/login");
      return;
    }

    // Validation
    if (!formData.checkInDate || !formData.checkOutDate) {
      toast.error("Please select check-in and check-out dates");
      return;
    }

    if (!formData.checkInTime || !formData.checkOutTime) {
      toast.error("Please select check-in and check-out times");
      return;
    }

    if (new Date(formData.checkOutDate) <= new Date(formData.checkInDate)) {
      toast.error("Check-out date must be after check-in date");
      return;
    }

    if (!formData.fullName || !formData.email || !formData.phone) {
      toast.error("Please fill in all required contact details");
      return;
    }

    setSubmitting(true);
    try {
      // Calculate pricing details
      const pricingDetails = calculateTotal();

      // Prepare booking data for payment order creation
      const bookingData = {
        userId: userData.id || userData.user_id || userData.userId,
        room: {
          roomId: room.roomId,
          roomType: room.roomType,
          pricePerNight: room.pricePerNight,
        },
        firstName: formData.fullName.split(" ")[0],
        lastName: formData.fullName.split(" ").slice(1).join(" ") || "",
        email: formData.email,
        phone: formData.phone,
        country: "IN",
        checkIn: `${formData.checkInDate}T${formData.checkInTime}:00`,
        checkOut: `${formData.checkOutDate}T${formData.checkOutTime}:00`,
        numberOfRooms: 1,
        guestCount: parseInt(formData.guests),
        specialRequests: formData.specialRequests,
        totalAmount: pricingDetails.total,
        membershipTier: membershipTier,
        discount: pricingDetails.discount,
        taxEnabled: pricingDetails.taxEnabled,
        taxInclusive: pricingDetails.taxInclusive,
        taxPercentage: pricingDetails.taxPercentage,
        taxName: pricingDetails.taxName,
        taxAmount: pricingDetails.taxAmount,
        amountAfterDiscount: pricingDetails.amountAfterDiscount,
        platformFee: pricingDetails.platformFee,
      };

      // Step 1: Create payment order (no booking saved yet)
      const createPaymentUrl = new URL(
        `${API_CONFIG.BASE_URL}/room-bookings/create-with-payment`
      );
      createPaymentUrl.searchParams.append("verifyBaseUrl", APP_URLS.BOOKING_VERIFY_QR);

      const response = await fetch(
        createPaymentUrl.toString(),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(bookingData),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create payment order");
      }

      const {
        razorpayOrder,
        razorpayKey,
        pnr,
        bookingData: serverBookingData,
      } = await response.json();

      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        toast.error(
          "Payment system not loaded. Please refresh the page and try again.",
        );
        setSubmitting(false);
        return;
      }

      // Step 2: Initialize Razorpay payment
      const options = {
        key: razorpayKey,
        amount: razorpayOrder.amount,
        currency: "INR",
        name: "Trivana Hospitality",
        description: `Room Booking - ${room.roomType}`,
        order_id: razorpayOrder.orderId,
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
        },
        handler: async (paymentResponse) => {
          try {
            // Step 3: Verify payment and create booking in database
            const verifyUrl = new URL(
              `${API_CONFIG.BASE_URL}/room-bookings/verify-payment-and-create`
            );
            verifyUrl.searchParams.append("verifyBaseUrl", APP_URLS.BOOKING_VERIFY_QR);

            const verifyResponse = await fetch(
              verifyUrl.toString(),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  razorpay_order_id: paymentResponse.razorpay_order_id,
                  razorpay_payment_id: paymentResponse.razorpay_payment_id,
                  razorpay_signature: paymentResponse.razorpay_signature,
                  pnr: pnr,
                  bookingData: serverBookingData,
                }),
              },
            );

            if (!verifyResponse.ok) {
              throw new Error("Payment verification failed");
            }

            const verifyData = await verifyResponse.json();
            const createdBooking = verifyData.booking;
            const finalPnr = verifyData.pnr;

            // Show success message
            toast.success(
              `🎉 Room booking confirmed successfully!\n📋 Your PNR: ${finalPnr}\n📧 Confirmation email sent with PDF receipt!`,
              { autoClose: 5000 },
            );

            // Navigate to profile/bookings page after success
            setTimeout(() => {
              navigate("/profile", { state: { activeTab: "bookings" } });
            }, 2000);
          } catch (error) {
            console.error("❌ Payment verification failed:", error);
            toast.error(
              "Payment verification failed. Please contact support with your payment ID.",
            );
            setSubmitting(false);
          }
        },
        prefill: {
          name: formData.fullName,
          email: formData.email,
          contact: formData.phone,
        },
        notes: {
          hotel_name: room.hotelName,
          room_type: room.roomType,
          check_in: formData.checkIn,
          check_out: formData.checkOut,
        },
        theme: {
          color: "#EE2E24",
        },
        modal: {
          ondismiss: function () {
            toast.warning("Payment cancelled. Your booking was not completed.");
            setSubmitting(false);
          },
        },
        ...RAZORPAY_CHECKOUT_CONFIG,
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Booking failed:", error);
      toast.error(
        error.message || "Failed to create booking. Please try again.",
      );
      setSubmitting(false);
    }
  };

  // Get minimum dates
  const getMinCheckInDate = () => new Date().toISOString().split("T")[0];
  const getMinCheckOutDate = () => {
    if (formData.checkInDate) {
      const checkIn = new Date(formData.checkInDate);
      checkIn.setDate(checkIn.getDate() + 1);
      return checkIn.toISOString().split("T")[0];
    }
    return getMinCheckInDate();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">
            Loading booking details...
          </p>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600">Room not found</h2>
          <button
            onClick={() => navigate("/HotelRoomsPage")}
            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Browse Rooms
          </button>
        </div>
      </div>
    );
  }

  const pricing = calculateTotal();

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", fontFamily: "'Inter', sans-serif" }}>
      {/* Premium Header */}
      <div
        style={{
          background: "white",
          borderBottom: "2px solid #EE2E24",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
        className="sticky top-0 z-50 py-3"
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-red-600 rounded-lg flex items-center justify-center text-white font-bold shadow-md text-xs">T</div>
            <h1 className={`${isMobile ? 'text-sm' : 'text-xl'} font-black tracking-tight text-gray-900 uppercase`}>
              Review <span className="text-red-600">Booking</span>
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-widest">
            <span className="text-red-600">Review</span>
            <span className="w-8 h-[2px] bg-gray-200"></span>
            <span>Payment</span>
            <span className="w-8 h-[2px] bg-gray-200"></span>
            <span>Confirm</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 md:px-4 py-3 md:py-4">
        {/* Back Button - Sleek */}
        <button
          onClick={() => {
            const hotelId = hotel?.hotelId || hotel?.id || room?.hotelId || room?.hotel?.hotelId;
            lockScroll();
            if (hotelId) {
              try {
                if (typeof sessionStorage !== "undefined") {
                  sessionStorage.setItem("focusHotelId", String(hotelId));
                }
              } catch (e) { }
              navigate(`/HotelRoomsPage#h-${hotelId}`, {
                state: { hotel, room, focusOnCard: true },
              });
            } else {
              navigate(-1);
            }
          }}
          className={`${isMobile ? 'mb-3' : 'mb-5'} flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-100 text-gray-500 font-black text-[9px] md:text-[10px] uppercase tracking-widest rounded-full hover:bg-gray-50 hover:text-red-600 hover:border-red-100 transition-all duration-500 shadow-sm hover:shadow-md group`}
        >
          <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
          <span>{isMobile ? "Back" : "Return To Selection"}</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          {/* Left Section - Elegant Booking Form (3 columns) */}
          <div className="lg:col-span-3 order-2 lg:order-1 flex flex-col">
            <div
              className={`rounded-2xl md:rounded-3xl overflow-hidden border border-white flex-grow flex flex-col`}
              style={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 15px 40px rgba(0, 0, 0, 0.04)",
              }}
            >
              {/* Step Header - Sleek & Modern */}
              <div
                className={`${isMobile ? 'px-4 py-3' : 'px-8 py-4'} flex items-center justify-between border-b border-gray-50`}
                style={{ background: "linear-gradient(to right, #ffffff, #fffafa)" }}
              >
                <div className="flex items-center gap-3">
                  <div className={`${isMobile ? 'w-8 h-8' : 'w-9 h-9'} bg-red-600/10 rounded-xl flex items-center justify-center text-red-600 font-extrabold shadow-sm`}>
                    1
                  </div>
                  <div>
                    <h2 className={`${isMobile ? 'text-xs' : 'text-sm'} font-black text-gray-900 leading-none`}>Guest Information</h2>
                    <p className={`${isMobile ? 'text-[8px]' : 'text-[9px]'} text-gray-400 font-bold uppercase tracking-[0.2em] mt-1`}>Personal Details</p>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1 bg-green-500/10 rounded-full`}>
                  <FaShieldAlt className="text-green-500 text-[8px] md:text-[10px]" />
                  <span className={`${isMobile ? 'text-[7px]' : 'text-[9px]'} font-black text-green-600 uppercase tracking-tighter`}>SECURE STAY</span>
                </div>
              </div>

              <div className={`${isMobile ? 'p-4' : 'p-7'} flex-grow`}>
                <form onSubmit={handleSubmit} className={`${isMobile ? 'gap-4' : 'space-y-5'} flex flex-col`}>
                  {/* Personal Details Section */}
                  <div className={`grid grid-cols-1 md:grid-cols-2 ${isMobile ? 'gap-4' : 'gap-5'}`}>
                    <div className="relative group">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1 mb-1.5 block group-focus-within:text-red-500 transition-colors">Full Name</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          placeholder="Your Name"
                          className="w-full pl-10 pr-4 py-2.5 text-[10px] md:text-xs border border-gray-100 rounded-2xl focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-50/50 transition-all font-bold text-gray-800 bg-gray-50/20"
                          required
                        />
                        <FaUsers className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-red-600 transition-colors text-xs" />
                      </div>
                    </div>
                    <div className="relative group">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1 mb-1.5 block group-focus-within:text-red-500 transition-colors">Email Address</label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Email Address"
                          className="w-full pl-10 pr-4 py-2.5 text-[10px] md:text-xs border border-gray-100 rounded-2xl focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-50/50 transition-all font-bold text-gray-800 bg-gray-50/20"
                          required
                        />
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-red-600 transition-colors font-black text-xs">@</div>
                      </div>
                    </div>
                  </div>

                  <div className={`grid grid-cols-1 md:grid-cols-2 ${isMobile ? 'gap-3' : 'gap-6'}`}>
                    <div className="relative group">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1 mb-2 block group-focus-within:text-red-500 transition-colors">Contact Number</label>
                      <div className="flex gap-2">
                        <div className="relative w-20 md:w-24">
                          <select className="w-full appearance-none pl-3 md:pl-4 pr-6 md:pr-8 py-2 md:py-3 text-[10px] md:text-xs border-2 border-gray-100 rounded-2xl bg-gray-50/30 font-extrabold text-gray-800 focus:outline-none focus:border-red-500">
                            <option>+91</option>
                          </select>
                          <div className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                            <svg width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L4 4L7 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                          </div>
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="Phone Number"
                          className="flex-1 px-4 py-2 md:py-3 text-[9px] md:text-xs border-2 border-gray-100 rounded-2xl focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-50/50 transition-all font-bold text-gray-800 bg-gray-50/30"
                          required
                        />
                      </div>
                    </div>
                    <div className="relative group">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1 mb-2 block group-focus-within:text-red-500 transition-colors">Guest Count</label>
                      <div className="relative">
                        <select
                          name="guests"
                          value={formData.guests}
                          onChange={handleInputChange}
                          className="w-full pl-11 pr-10 py-2 md:py-3 text-[9px] md:text-xs border-2 border-gray-100 rounded-2xl focus:outline-none focus:border-red-500 transition-all font-bold text-gray-800 bg-gray-50/30 appearance-none"
                          required
                        >
                          {[...Array(room.maxPersons || 4)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1} {i === 0 ? "Person" : "Persons"}
                            </option>
                          ))}
                        </select>
                        <FaUsers className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-red-600 transition-colors" />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                          <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.5L5 5.5L9 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Travel Schedule Section */}
                  <div className={`${isMobile ? 'p-3' : 'p-6'} bg-red-50/50 rounded-[1.5rem] md:rounded-[2rem] border-2 border-red-100 relative overflow-hidden group`}>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full -translate-x-[-20%] -translate-y-[50%] blur-3xl"></div>
                    <div className="relative grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className={`md:col-span-2 grid grid-cols-2 ${isMobile ? 'gap-2' : 'gap-3'}`}>
                        <div className="space-y-1">
                          <label className="text-[7.5px] md:text-[9px] font-black text-red-500/60 uppercase tracking-widest px-1">Check-in</label>
                          <input
                            type="date"
                            name="checkInDate"
                            value={formData.checkInDate}
                            onChange={handleInputChange}
                            min={getMinCheckInDate()}
                            className="w-full px-1.5 py-1.5 text-[8.5px] md:text-[11px] border-2 border-white rounded-xl focus:outline-none focus:border-red-500 transition-all font-black text-gray-800 bg-white"
                            required
                          />
                        </div>
                        <div className="space-y-1 text-right">
                          <label className="text-[7.5px] md:text-[9px] font-black text-red-500/60 uppercase tracking-widest px-1">Arrival Time</label>
                          <select
                            name="checkInTime"
                            value={formData.checkInTime}
                            onChange={handleInputChange}
                            className="w-full px-1.5 py-1.5 text-[8.5px] md:text-[11px] border-2 border-white rounded-xl focus:outline-none focus:border-red-500 transition-all font-black text-gray-800 bg-white"
                            required
                          >
                            <option value="">{isMobile ? "Arr." : "Select Arrival"}</option>
                            {getCheckInTimeSlots().map((slot) => (
                              <option key={slot.value} value={slot.value}>{slot.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className={`md:col-span-2 grid grid-cols-2 ${isMobile ? 'gap-2' : 'gap-3'}`}>
                        <div className="space-y-1">
                          <label className="text-[7.5px] md:text-[9px] font-black text-red-500/60 uppercase tracking-widest px-1">Check-out</label>
                          <input
                            type="date"
                            name="checkOutDate"
                            value={formData.checkOutDate}
                            onChange={handleInputChange}
                            min={getMinCheckOutDate()}
                            className="w-full px-1.5 py-1.5 text-[8.5px] md:text-[11px] border-2 border-white rounded-xl focus:outline-none focus:border-red-500 transition-all font-black text-gray-800 bg-white shadow-sm"
                            required
                          />
                        </div>
                        <div className="space-y-1 text-right">
                          <label className="text-[7.5px] md:text-[9px] font-black text-red-500/60 uppercase tracking-widest px-1">Departure</label>
                          <select
                            name="checkOutTime"
                            value={formData.checkOutTime}
                            onChange={handleInputChange}
                            className="w-full px-1.5 py-1.5 text-[8.5px] md:text-[11px] border-2 border-white rounded-xl focus:outline-none focus:border-red-500 transition-all font-black text-gray-800 bg-white shadow-sm"
                            required
                          >
                            <option value="">{isMobile ? "Dep." : "Select Departure"}</option>
                            {getCheckOutTimeSlots().map((slot) => (
                              <option key={slot.value} value={slot.value}>{slot.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Special Requests */}
                  <div className="space-y-1 group">
                    <label className="text-[8px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest px-1 group-focus-within:text-red-500 transition-colors">Special Requests (Optional)</label>
                    <input
                      type="text"
                      name="specialRequests"
                      value={formData.specialRequests}
                      onChange={handleInputChange}
                      placeholder={isMobile ? "e.g. Early check-in..." : "e.g. Early check-in, High floor, Anniversary celebration..."}
                      className="w-full px-4 py-2 text-[9px] md:text-xs border-2 border-gray-100 rounded-2xl focus:outline-none focus:border-red-500 transition-all font-bold text-gray-800 bg-gray-50/30"
                    />
                  </div>

                  {/* Submit Area - High Fidelity */}
                  <div className={`${isMobile ? 'pt-3' : 'pt-4 border-t'} border-gray-100 mt-auto`}>
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`w-full ${isMobile ? 'py-3.5' : 'py-3'} px-6 rounded-xl font-black text-[11px] md:text-xs text-white transition-all duration-300 transform hover:translate-y-[-1px] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl flex items-center justify-center gap-2 group relative overflow-hidden`}
                      style={{
                        background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                        boxShadow: "0 10px 30px rgba(238, 46, 36, 0.25)",
                      }}
                    >
                      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                      <div className="absolute -left-10 top-0 w-20 h-full bg-white/10 skew-x-[-25deg] group-hover:left-[110%] transition-all duration-800"></div>
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                          <span className="tracking-widest uppercase">Securing...</span>
                        </>
                      ) : (
                        <>
                          <FaShieldAlt className="text-white/70 text-[10px]" />
                          <span className="tracking-widest uppercase">{isMobile ? "PAY NOW" : "Finalize & Pay Now"}</span>
                          <span className="text-sm transition-transform group-hover:translate-x-1">→</span>
                        </>
                      )}
                    </button>
                    <div className="flex items-center justify-center gap-3 mt-3 px-2 opacity-60">
                      <p className="text-[7.5px] md:text-[8px] text-gray-500 font-black uppercase tracking-widest flex items-center gap-1">
                        <FaShieldAlt className="text-green-500 text-[6px] md:text-[7px]" /> SSL Secure
                      </p>
                      <div className="h-2 w-[1px] bg-gray-300"></div>
                      <p className="text-[7.5px] md:text-[8px] text-gray-500 font-black uppercase tracking-widest flex items-center gap-1">
                        <FaCheckCircle className="text-blue-500 text-[6px] md:text-[7px]" /> Instant
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Right Section - Elegant Booking Summary (2 columns) */}
          <div className="lg:col-span-2 order-1 lg:order-2 flex flex-col">
            <div
              className="rounded-3xl overflow-hidden sticky top-24 border border-white flex-grow flex flex-col"
              style={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 50px rgba(0,0,0,0.06)",
              }}
            >
              {/* Room Image - More Premium */}
              <div className={`relative ${isMobile ? 'h-36' : 'h-48'} group overflow-hidden`}>
                <img
                  src={room?.photoUrl || "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&auto=format&fit=crop&q=80"}
                  alt="Room"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                  <div className="bg-white/20 backdrop-blur-md border border-white/30 rounded-full px-2.5 py-0.5 flex items-center gap-1 shadow-lg">
                    <FaStar className="text-yellow-400 text-[10px]" />
                    <span className="font-black text-[9px] text-white">4.8 (Highly Rated)</span>
                  </div>
                  <div className="bg-red-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-md shadow-lg">
                    FEW LEFT
                  </div>
                </div>
              </div>

              <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
                {/* Hotel & Room Details */}
                <div className={`${isMobile ? 'mb-2' : 'mb-4'}`}>
                  <h3 className={`${isMobile ? 'text-xs' : 'text-base'} font-black text-gray-900 leading-tight mb-0.5 uppercase tracking-tight`}>
                    {hotel?.hotelName || "Trivana Premium Hotel"}
                  </h3>
                  <div className="flex items-center gap-1 text-[9px] text-gray-400 font-bold uppercase tracking-wide">
                    <FaMapMarkerAlt className="text-red-500" />
                    <span className="line-clamp-1">{hotel?.city || "Luxury Destination"}</span>
                  </div>
                </div>

                {/* Booking Timeline */}
                <div className={`${isMobile ? 'mb-3 p-3' : 'mb-4 p-4'} bg-gray-50/50 border border-gray-100 rounded-2xl`}>
                  <div className={`grid grid-cols-2 ${isMobile ? 'gap-2' : 'gap-3'} relative`}>
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white border border-gray-100 rounded-full flex items-center justify-center text-[10px] text-blue-500 shadow-sm z-10">
                      <FaCalendarAlt />
                    </div>

                    <div className="space-y-1">
                      <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Check-In</p>
                      <p className="text-xs font-black text-gray-900">
                        {formData.checkInDate ? new Date(formData.checkInDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "Select"}
                      </p>
                    </div>

                    <div className="space-y-1 text-right">
                      <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Check-Out</p>
                      <p className="text-xs font-black text-gray-900">
                        {formData.checkOutDate ? new Date(formData.checkOutDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "Select"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] font-bold text-gray-600">
                    <span className="flex items-center gap-1.5"><FaBed className="text-red-500" /> {room?.roomType || "Signature Suite"}</span>
                    <span className="flex items-center gap-1.5"><FaUsers className="text-red-500" /> {formData.guests} Guest(s)</span>
                  </div>
                </div>

                {/* Pricing Summary - High Fidelity */}
                <div className="bg-gray-50/30 rounded-2xl p-4 border border-gray-100">
                  <div className="space-y-2.5">
                    <div className="flex justify-between items-center text-[11px]">
                      <span className="font-bold text-gray-500">{pricing.nights} Night(s) Multi-Stay</span>
                      <span className="font-black text-gray-900">₹{pricing.basePrice.toLocaleString("en-IN")}</span>
                    </div>

                    {pricing.discount > 0 && (
                      <div className="flex justify-between items-center text-[11px]">
                        <span className="font-bold text-green-600 flex items-center gap-1.5 truncate pr-2">
                          <FaPercentage className="text-[9px]" /> Member Privilege
                        </span>
                        <span className="font-black text-green-600">-₹{pricing.discount.toLocaleString("en-IN")}</span>
                      </div>
                    )}

                    {pricing.taxEnabled && (
                      <div className="flex justify-between items-center text-[11px]">
                        <span className="font-bold text-gray-500">
                          {pricing.taxName} ({pricing.taxPercentage}%)
                          {pricing.taxInclusive ? " Included" : ""}
                        </span>
                        <span className={`font-black ${pricing.taxInclusive ? "text-gray-500" : "text-gray-900"}`}>
                          {pricing.taxInclusive ? "Incl. ₹" : "₹"}
                          {pricing.taxAmount.toLocaleString("en-IN")}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between items-center text-[11px]">
                      <span className="font-bold text-gray-500">Platform Fee</span>
                      <span className="font-black text-gray-900">₹{pricing.platformFee.toLocaleString("en-IN")}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-dashed border-gray-200">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Total Payable</p>
                        <p className="text-xl font-black text-gray-900 tracking-tighter leading-none">₹{pricing.total.toLocaleString("en-IN")}</p>
                      </div>
                      {pricing.discount > 0 && (
                        <div className="bg-green-500 text-white text-[9px] font-black px-2 py-1 rounded-sm shadow-sm scale-90 origin-right">
                          YOU SAVE ₹{pricing.discount.toLocaleString("en-IN")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Freebies - Sleek Horizontal */}
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-[9px] font-black text-gray-600 uppercase tracking-tighter">High-Speed WiFi</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    <span className="text-[9px] font-black text-gray-600 uppercase tracking-tighter">Flexible Stay</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomBookingPage;
